import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Printer,
  Zap,
  Upload,
  Check,
  X,
  ExternalLink,
  FileImage,
  RefreshCw,
  Palette,
  AlertCircle,
  CheckCircle,
  Info,
  Image as ImageIcon,
  Package,
  Truck,
  DollarSign,
  Camera,
  Plus,
  Trash2,
  Settings
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import LoadingSpinner from '../ui/LoadingSpinner';
import './PrintQualityPage.css';

const PrintQualityPage = () => {
  const { vcId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [artwork, setArtwork] = useState(null);
  const [printStatus, setPrintStatus] = useState(null);
  const [scanningQuality, setScanningQuality] = useState(false);
  const [uploadingHighRes, setUploadingHighRes] = useState(false);
  const [analyzingColors, setAnalyzingColors] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const highResInputRef = useRef(null);
  const inventoryPhotoInputRef = useRef(null);

  // Track selection: 'on_demand' or 'artist_inventory'
  const [activeTrack, setActiveTrack] = useState('on_demand');
  
  // Artist inventory state
  const [inventorySizes, setInventorySizes] = useState({});
  const [inventoryPhotos, setInventoryPhotos] = useState([]);
  const [commissionRate, setCommissionRate] = useState(0.15);
  const [uploadingInventoryPhoto, setUploadingInventoryPhoto] = useState(false);
  const [savingInventory, setSavingInventory] = useState(false);

  // Print quality constants - 240 DPI for professional prints
  const PRINT_DIMENSIONS_240DPI = {
    '5x7': { width: 1200, height: 1680, minPx: 1200, label: '5×7"' },
    '8x10': { width: 1920, height: 2400, minPx: 1920, label: '8×10"' },
    '11x14': { width: 2640, height: 3360, minPx: 2640, label: '11×14"' },
    '12x12': { width: 2880, height: 2880, minPx: 2880, label: '12×12"' },
    '16x20': { width: 3840, height: 4800, minPx: 3840, label: '16×20"' },
    '18x18': { width: 4320, height: 4320, minPx: 4320, label: '18×18"' },
    '20x30': { width: 4800, height: 7200, minPx: 4800, label: '20×30"' }
  };

  // Load artwork and print status on mount
  useEffect(() => {
    loadArtworkData();
  }, [vcId]);

  const loadArtworkData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load artwork data
      const { data: artworkData, error: artworkError } = await supabase
        .from('vcs')
        .select(`
          vc_id,
          title,
          description,
          cover_image,
          price,
          width,
          height,
          depth,
          color_palette,
          users (
            uuid,
            nickname,
            first_name,
            last_name,
            handle
          )
        `)
        .eq('vc_id', vcId)
        .single();

      if (artworkError) throw artworkError;
      setArtwork(artworkData);

      // Load print status
      const { data: statusData, error: statusError } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      if (statusError && statusError.code !== 'PGRST116') throw statusError;
      setPrintStatus(statusData);
      
      // Load artist inventory data if exists
      if (statusData) {
        setInventorySizes(statusData.artist_inventory_sizes || {});
        setInventoryPhotos(statusData.artist_inventory_photos || []);
        setCommissionRate(statusData.platform_commission_rate || 0.15);
        // Set active track based on what's enabled
        if (statusData.artist_inventory_enabled && !statusData.on_demand_enabled) {
          setActiveTrack('artist_inventory');
        }
      }
    } catch (err) {
      console.error('Error loading artwork data:', err);
      setError('Failed to load artwork: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate quality info from dimensions
  const calculateQualityInfo = (width, height) => {
    const maxDim = Math.max(width || 0, height || 0);
    let qualityLevel = 'low';
    if (maxDim >= 4800) qualityLevel = 'excellent';
    else if (maxDim >= 3600) qualityLevel = 'good';
    else if (maxDim >= 2640) qualityLevel = 'acceptable';

    const availableSizes = [];
    Object.entries(PRINT_DIMENSIONS_240DPI).forEach(([size, dims]) => {
      const fitsLandscape = width >= dims.width && height >= dims.height;
      const fitsPortrait = width >= dims.height && height >= dims.width;
      if (fitsLandscape || fitsPortrait) {
        availableSizes.push(size);
      }
    });

    return { qualityLevel, availableSizes };
  };

  // Get quality badge color
  const getQualityColor = (level) => {
    switch (level) {
      case 'excellent': return '#16a34a';
      case 'good': return '#2563eb';
      case 'acceptable': return '#ca8a04';
      case 'low': return '#dc2626';
      default: return '#6b7280';
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Build Cloudinary analysis URL - works with both Cloudinary and external URLs
  const buildCloudinaryAnalysisUrl = (imageUrl) => {
    const cloudName = 'vibrantcast';
    
    if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/image/upload/')) {
      let analysisUrl = imageUrl.replace('/image/upload/', '/image/upload/fl_getinfo/');
      if (!analysisUrl.includes('.json')) {
        analysisUrl = analysisUrl.replace(/\.[^/.]+$/, '.json');
      }
      return analysisUrl;
    } else {
      const encodedUrl = encodeURIComponent(imageUrl);
      return `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodedUrl}`;
    }
  };

  // Get image dimensions from file (browser fallback)
  const getImageDimensionsFromFile = (file) => {
    return new Promise((resolve, reject) => {
      // Only works for browser-supported formats (not TIFF)
      if (file.type === 'image/tiff' || file.name.toLowerCase().match(/\.tiff?$/)) {
        resolve({ width: 0, height: 0 });
        return;
      }
      
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not load image'));
      };
      img.src = url;
    });
  };

  // Scan existing image for quality
  const scanExistingImageQuality = async () => {
    const imageToScan = printStatus?.highres_image_url || artwork?.cover_image;
    
    if (!imageToScan) {
      setError('No image to scan. Please add a cover image first.');
      return;
    }

    setScanningQuality(true);
    setError('');
    setSuccess('');

    try {
      const infoUrl = buildCloudinaryAnalysisUrl(imageToScan);
      console.log('Scanning image via Cloudinary:', infoUrl);

      const response = await fetch(infoUrl);
      if (!response.ok) throw new Error('Failed to get image info from Cloudinary');

      const data = await response.json();
      const { width, height, bytes } = data.input || data;

      const { qualityLevel, availableSizes } = calculateQualityInfo(width, height);

      // Save or update print status
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id, highres_image_url')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: Number(vcId),
        optin_id: null,
        image_width: width,
        image_height: height,
        image_bytes: bytes,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      if (existing?.highres_image_url) {
        record.highres_image_url = existing.highres_image_url;
      }

      if (existing) {
        await supabase.from('artwork_print_status').update(record).eq('id', existing.id);
      } else {
        await supabase.from('artwork_print_status').insert(record);
      }

      // Reload print status
      const { data: updated } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();
      
      setPrintStatus(updated);
      
      const sourceLabel = printStatus?.highres_image_url ? 'High-res' : 'Cover image';
      setSuccess(`${sourceLabel} scanned: ${width?.toLocaleString()} × ${height?.toLocaleString()}px (${qualityLevel})`);
    } catch (err) {
      console.error('Error scanning image:', err);
      setError('Failed to scan image: ' + err.message);
    } finally {
      setScanningQuality(false);
    }
  };

  // Handle high-res image upload
  const handleHighResUpload = async (file) => {
    if (!file) return;

    setUploadingHighRes(true);
    setError('');
    setSuccess('');

    try {
      // Check file size (100MB limit for admin)
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 100) {
        throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum is 100MB.`);
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/heic', 'image/heif', 'image/webp'];
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(jpe?g|png|tiff?|heic|heif|webp)$/)) {
        throw new Error('Invalid file type. Supported: JPEG, PNG, TIFF, HEIC, HEIF, WebP');
      }

      // Generate unique filename
      const timestamp = Date.now();
      const extension = file.name.split('.').pop().toLowerCase();
      const fileName = `highres_${vcId}_${timestamp}.${extension}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('highres_prints')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) throw new Error(uploadError.message);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('highres_prints')
        .getPublicUrl(fileName);

      console.log('Uploaded high-res image:', publicUrl);

      // Try to analyze with Cloudinary
      let imageDimensions = { width: 0, height: 0 };
      let imageBytes = file.size;
      
      try {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Wait for file availability
        
        const cloudName = 'vibrantcast';
        
        // For TIFF files, Cloudinary fetch may not work well
        // Try to use a converted version or fall back to browser analysis
        const isTiff = file.type === 'image/tiff' || file.name.toLowerCase().match(/\.tiff?$/);
        
        if (!isTiff) {
          const analysisUrl = `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodeURIComponent(publicUrl)}`;
          const response = await fetch(analysisUrl);
          if (response.ok) {
            const data = await response.json();
            const info = data.input || data;
            imageDimensions.width = info.width || 0;
            imageDimensions.height = info.height || 0;
            imageBytes = info.bytes || file.size;
          }
        }
        
        // Fallback for TIFF or if Cloudinary fails
        if (imageDimensions.width === 0) {
          try {
            imageDimensions = await getImageDimensionsFromFile(file);
          } catch {
            console.log('Browser fallback failed for dimensions');
          }
        }
      } catch (analysisErr) {
        console.log('Image analysis error:', analysisErr);
      }

      const { qualityLevel, availableSizes } = calculateQualityInfo(imageDimensions.width, imageDimensions.height);

      // Check if record exists
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: Number(vcId),
        optin_id: null,
        highres_image_url: publicUrl,
        image_width: imageDimensions.width,
        image_height: imageDimensions.height,
        image_bytes: imageBytes,
        quality_level: imageDimensions.width > 0 ? qualityLevel : 'pending',
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low' && imageDimensions.width > 0
      };

      if (existing) {
        await supabase.from('artwork_print_status').update(record).eq('id', existing.id);
      } else {
        await supabase.from('artwork_print_status').insert(record);
      }

      // Reload print status
      const { data: updated } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();
      
      setPrintStatus(updated);

      const dimDisplay = imageDimensions.width > 0 
        ? `${imageDimensions.width?.toLocaleString()} × ${imageDimensions.height?.toLocaleString()}px (${qualityLevel})`
        : 'Uploaded! Click "Re-Scan" to analyze dimensions.';
      setSuccess(`High-res image uploaded! ${dimDisplay}`);
    } catch (err) {
      console.error('Error uploading high-res image:', err);
      setError('Failed to upload: ' + err.message);
    } finally {
      setUploadingHighRes(false);
    }
  };

  // Analyze colors using Cloudinary
  const analyzeImageColors = async () => {
    const imageUrl = printStatus?.highres_image_url || artwork?.cover_image;
    if (!imageUrl) {
      setError('No image available for color analysis.');
      return;
    }

    // Check if this is a TIFF file - Cloudinary color analysis doesn't work well with TIFF
    const isTiff = imageUrl.toLowerCase().match(/\.tiff?$/);
    if (isTiff) {
      setError('Color analysis is not supported for TIFF files. Please use a JPEG or PNG version, or scan the cover image instead.');
      return;
    }

    setAnalyzingColors(true);
    setError('');
    setSuccess('');

    try {
      const analysisUrl = buildCloudinaryAnalysisUrl(imageUrl);
      console.log('Analyzing colors via:', analysisUrl);

      const response = await fetch(analysisUrl);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cloudinary response error:', response.status, errorText);
        throw new Error(`Cloudinary analysis failed (${response.status}). Try using the cover image instead.`);
      }

      const data = await response.json();
      
      // Extract color information
      const colors = data.colors || data.predominant?.google || [];
      
      if (colors.length > 0) {
        const colorPalette = {};
        colors.slice(0, 6).forEach((colorInfo, index) => {
          const colorName = colorInfo[0] || `color_${index}`;
          const percentage = colorInfo[1] || 0;
          if (!colorPalette[colorName]) {
            colorPalette[colorName] = [];
          }
          colorPalette[colorName].push({
            hex: colorInfo[0],
            percentage: parseFloat(percentage)
          });
        });

        await supabase
          .from('vcs')
          .update({ color_palette: colorPalette })
          .eq('vc_id', vcId);

        // Refresh artwork data
        const { data: updatedArtwork } = await supabase
          .from('vcs')
          .select('color_palette')
          .eq('vc_id', vcId)
          .single();
        
        if (updatedArtwork) {
          setArtwork(prev => ({ ...prev, color_palette: updatedArtwork.color_palette }));
        }

        setSuccess(`Color analysis complete! Found ${colors.length} dominant colors.`);
      } else {
        setSuccess('Color analysis complete. No dominant colors detected in the response.');
      }
    } catch (err) {
      console.error('Error analyzing colors:', err);
      setError('Color analysis failed: ' + err.message);
    } finally {
      setAnalyzingColors(false);
    }
  };

  // Toggle print enabled status
  const togglePrintEnabled = async () => {
    if (!printStatus) return;
    
    try {
      const newStatus = !printStatus.print_enabled;
      await supabase
        .from('artwork_print_status')
        .update({ print_enabled: newStatus })
        .eq('id', printStatus.id);
      
      setPrintStatus(prev => ({ ...prev, print_enabled: newStatus }));
      setSuccess(`Print sales ${newStatus ? 'enabled' : 'disabled'} for this artwork.`);
    } catch (err) {
      console.error('Error toggling print status:', err);
      setError('Failed to update print status.');
    }
  };

  // Handle inventory photo upload
  const handleInventoryPhotoUpload = async (file) => {
    if (!file) return;

    setUploadingInventoryPhoto(true);
    setError('');

    try {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 20) {
        throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum is 20MB.`);
      }

      const timestamp = Date.now();
      const extension = file.name.split('.').pop().toLowerCase();
      const fileName = `inventory_${vcId}_${timestamp}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from('highres_prints')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) throw new Error(uploadError.message);

      const { data: { publicUrl } } = supabase.storage
        .from('highres_prints')
        .getPublicUrl(fileName);

      const newPhoto = {
        url: publicUrl,
        caption: '',
        size: '',
        uploaded_at: new Date().toISOString()
      };

      setInventoryPhotos(prev => [...prev, newPhoto]);
      setSuccess('Photo uploaded! Add caption and size info below.');
    } catch (err) {
      console.error('Error uploading inventory photo:', err);
      setError('Failed to upload photo: ' + err.message);
    } finally {
      setUploadingInventoryPhoto(false);
    }
  };

  // Remove inventory photo
  const removeInventoryPhoto = (index) => {
    setInventoryPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Update inventory photo metadata
  const updateInventoryPhoto = (index, field, value) => {
    setInventoryPhotos(prev => prev.map((photo, i) => 
      i === index ? { ...photo, [field]: value } : photo
    ));
  };

  // Add/update inventory size
  const updateInventorySize = (size, field, value) => {
    setInventorySizes(prev => ({
      ...prev,
      [size]: {
        ...prev[size],
        [field]: field === 'price' || field === 'quantity' || field === 'edition_limit' 
          ? (value === '' ? null : Number(value)) 
          : value
      }
    }));
  };

  // Remove inventory size
  const removeInventorySize = (size) => {
    setInventorySizes(prev => {
      const newSizes = { ...prev };
      delete newSizes[size];
      return newSizes;
    });
  };

  // Save artist inventory settings
  const saveInventorySettings = async () => {
    setSavingInventory(true);
    setError('');
    setSuccess('');

    try {
      // Validate at least one size has price and quantity
      const validSizes = Object.entries(inventorySizes).filter(
        ([_, data]) => data.price > 0 && data.quantity > 0
      );

      if (validSizes.length === 0 && inventoryPhotos.length === 0) {
        throw new Error('Add at least one size with price/quantity, or upload a photo.');
      }

      const updateData = {
        artist_inventory_enabled: true,
        artist_inventory_photos: inventoryPhotos,
        artist_inventory_sizes: inventorySizes,
        artist_inventory_enrolled_at: printStatus?.artist_inventory_enrolled_at || new Date().toISOString(),
        platform_commission_rate: commissionRate,
        fulfillment_preference: printStatus?.on_demand_enabled ? 'both' : 'artist_inventory'
      };

      if (printStatus?.id) {
        await supabase
          .from('artwork_print_status')
          .update(updateData)
          .eq('id', printStatus.id);
      } else {
        await supabase
          .from('artwork_print_status')
          .insert({
            vc_id: Number(vcId),
            optin_id: null,
            ...updateData
          });
      }

      // Reload
      const { data: updated } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();
      
      setPrintStatus(updated);
      setSuccess('Artist inventory settings saved!');
    } catch (err) {
      console.error('Error saving inventory:', err);
      setError('Failed to save: ' + err.message);
    } finally {
      setSavingInventory(false);
    }
  };

  // Toggle artist inventory enabled
  const toggleArtistInventory = async () => {
    if (!printStatus) return;

    try {
      const newStatus = !printStatus.artist_inventory_enabled;
      const newPreference = newStatus 
        ? (printStatus.on_demand_enabled ? 'both' : 'artist_inventory')
        : (printStatus.on_demand_enabled ? 'on_demand' : 'none');

      await supabase
        .from('artwork_print_status')
        .update({ 
          artist_inventory_enabled: newStatus,
          fulfillment_preference: newPreference
        })
        .eq('id', printStatus.id);
      
      setPrintStatus(prev => ({ 
        ...prev, 
        artist_inventory_enabled: newStatus,
        fulfillment_preference: newPreference
      }));
      setSuccess(`Artist inventory ${newStatus ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      console.error('Error toggling artist inventory:', err);
      setError('Failed to update.');
    }
  };

  // Toggle on-demand enabled
  const toggleOnDemand = async () => {
    if (!printStatus) return;

    try {
      const newStatus = !printStatus.on_demand_enabled;
      const newPreference = newStatus 
        ? (printStatus.artist_inventory_enabled ? 'both' : 'on_demand')
        : (printStatus.artist_inventory_enabled ? 'artist_inventory' : 'none');

      await supabase
        .from('artwork_print_status')
        .update({ 
          on_demand_enabled: newStatus,
          on_demand_enrolled_at: newStatus ? new Date().toISOString() : printStatus.on_demand_enrolled_at,
          fulfillment_preference: newPreference
        })
        .eq('id', printStatus.id);
      
      setPrintStatus(prev => ({ 
        ...prev, 
        on_demand_enabled: newStatus,
        fulfillment_preference: newPreference
      }));
      setSuccess(`On-demand printing ${newStatus ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      console.error('Error toggling on-demand:', err);
      setError('Failed to update.');
    }
  };

  // Format artist name
  const formatArtistName = (user) => {
    if (!user) return 'Unknown Artist';
    return (
      user.nickname ||
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      (user.handle ? `@${user.handle}` : '') ||
      'Unknown Artist'
    );
  };

  if (loading) {
    return (
      <div className="print-quality-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="print-quality-page">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Artwork Not Found</h2>
          <p>The requested artwork could not be found.</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft size={16} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="print-quality-page">
      {/* Header */}
      <div className="print-page-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="header-title">
          <Printer size={24} />
          <h1>Print Management</h1>
        </div>
      </div>

      {/* Track Selector Tabs */}
      <div className="track-selector">
        <button 
          className={`track-tab ${activeTrack === 'on_demand' ? 'active' : ''}`}
          onClick={() => setActiveTrack('on_demand')}
        >
          <Zap size={18} />
          <span>On-Demand Printing</span>
          {printStatus?.on_demand_enabled && <Check size={14} className="track-check" />}
        </button>
        <button 
          className={`track-tab ${activeTrack === 'artist_inventory' ? 'active' : ''}`}
          onClick={() => setActiveTrack('artist_inventory')}
        >
          <Package size={18} />
          <span>Artist Inventory</span>
          {printStatus?.artist_inventory_enabled && <Check size={14} className="track-check" />}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X size={16} /></button>
        </div>
      )}

      <div className="print-page-content">
        {/* Left Column - Artwork Preview */}
        <div className="artwork-preview-section">
          <div className="artwork-image-container">
            <img 
              src={artwork.cover_image} 
              alt={artwork.title}
              onError={(e) => { e.target.src = '/images/placeholder-artwork.jpg'; }}
            />
          </div>
          <div className="artwork-info">
            <h2>{artwork.title}</h2>
            <p className="artist-name">{formatArtistName(artwork.users)}</p>
            {artwork.width && artwork.height && (
              <p className="dimensions">
                {artwork.width}" × {artwork.height}"{artwork.depth ? ` × ${artwork.depth}"` : ''}
              </p>
            )}
            {artwork.price && (
              <p className="price">${parseFloat(artwork.price).toLocaleString()}</p>
            )}
          </div>
          
          {/* Color Palette Display */}
          {artwork.color_palette && Object.keys(artwork.color_palette).length > 0 && (
            <div className="color-palette-section">
              <h3><Palette size={16} /> Color Palette</h3>
              <div className="color-swatches">
                {Object.entries(artwork.color_palette).slice(0, 6).map(([colorName, colorData], idx) => {
                  const hexColor = Array.isArray(colorData) && colorData[0]?.hex ? colorData[0].hex : colorName;
                  const percentage = Array.isArray(colorData) && colorData[0]?.percentage ? colorData[0].percentage : null;
                  return (
                    <div 
                      key={idx} 
                      className="color-swatch"
                      style={{ backgroundColor: hexColor }}
                      title={`${colorName}${percentage ? ` (${percentage.toFixed(1)}%)` : ''}`}
                    >
                      {percentage && <span className="percentage">{percentage.toFixed(0)}%</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Print Status & Actions */}
        <div className="print-status-section">
          {/* On-Demand Track */}
          {activeTrack === 'on_demand' && (
            <>
          {/* Print Status Card */}
          <div className="status-card">
            <div className="status-card-header">
              <h3>Print Status</h3>
              {printStatus && (
                <div className="status-badges">
                  <span 
                    className="quality-badge"
                    style={{ backgroundColor: getQualityColor(printStatus.quality_level) }}
                  >
                    {printStatus.quality_level === 'pending' ? 'Pending Analysis' : printStatus.quality_level}
                  </span>
                  {printStatus.print_enabled && (
                    <span className="enabled-badge">
                      <Check size={12} /> Print Enabled
                    </span>
                  )}
                </div>
              )}
            </div>

            {printStatus ? (
              <div className="status-details">
                <div className="detail-row">
                  <span className="label">Image Dimensions:</span>
                  <span className="value">
                    {printStatus.image_width > 0 
                      ? `${printStatus.image_width?.toLocaleString()} × ${printStatus.image_height?.toLocaleString()}px`
                      : 'Not analyzed yet'}
                  </span>
                </div>
                {printStatus.image_bytes > 0 && (
                  <div className="detail-row">
                    <span className="label">File Size:</span>
                    <span className="value">{formatFileSize(printStatus.image_bytes)}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="label">Last Checked:</span>
                  <span className="value">
                    {printStatus.quality_checked_at 
                      ? new Date(printStatus.quality_checked_at).toLocaleString()
                      : 'Never'}
                  </span>
                </div>
                {printStatus.highres_image_url && (
                  <div className="detail-row">
                    <span className="label">High-Res Source:</span>
                    <a 
                      href={printStatus.highres_image_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="value link"
                    >
                      View Image <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-status">
                <FileImage size={32} />
                <p>No print quality data yet.</p>
                <p className="hint">Scan the cover image to check print eligibility, or upload a high-resolution source file.</p>
              </div>
            )}
          </div>

          {/* Available Print Sizes */}
          {printStatus?.available_sizes?.length > 0 && (
            <div className="sizes-card">
              <h3>Available Print Sizes (240 DPI)</h3>
              <div className="sizes-grid">
                {Object.entries(PRINT_DIMENSIONS_240DPI).map(([size, dims]) => {
                  const isAvailable = printStatus.available_sizes.includes(size);
                  return (
                    <div 
                      key={size} 
                      className={`size-tag ${isAvailable ? 'available' : 'unavailable'}`}
                    >
                      {isAvailable ? <Check size={12} /> : <X size={12} />}
                      <span>{dims.label}</span>
                      <small>{dims.minPx}px min</small>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="actions-card">
            <h3>Actions</h3>
            
            <input
              type="file"
              ref={highResInputRef}
              style={{ display: 'none' }}
              accept="image/jpeg,image/png,image/tiff,image/heic,image/heif,image/webp"
              onChange={(e) => handleHighResUpload(e.target.files?.[0])}
            />

            <div className="action-buttons">
              <button
                className="action-btn primary"
                onClick={() => highResInputRef.current?.click()}
                disabled={uploadingHighRes}
              >
                {uploadingHighRes ? (
                  <><RefreshCw className="spin" size={18} /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload High-Res Source</>
                )}
              </button>
              
              <button
                className="action-btn secondary"
                onClick={scanExistingImageQuality}
                disabled={scanningQuality}
              >
                {scanningQuality ? (
                  <><RefreshCw className="spin" size={18} /> Scanning...</>
                ) : (
                  <><Zap size={18} /> {printStatus?.highres_image_url ? 'Re-Scan High-Res' : 'Scan Cover Image'}</>
                )}
              </button>
              
              <button
                className="action-btn secondary"
                onClick={analyzeImageColors}
                disabled={analyzingColors}
              >
                {analyzingColors ? (
                  <><RefreshCw className="spin" size={18} /> Analyzing...</>
                ) : (
                  <><Palette size={18} /> Analyze Colors</>
                )}
              </button>

              {printStatus && (
                <button
                  className={`action-btn ${printStatus.print_enabled ? 'danger' : 'success'}`}
                  onClick={togglePrintEnabled}
                >
                  {printStatus.print_enabled ? (
                    <><X size={18} /> Disable Print Sales</>
                  ) : (
                    <><Check size={18} /> Enable Print Sales</>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="tips-card">
            <h3><Info size={16} /> Print Quality Tips</h3>
            <ul>
              <li><strong>For 20" prints:</strong> Need 4,800px minimum on longest side (240 DPI)</li>
              <li><strong>Recommended workflow:</strong> Scan cover image first. If quality is low, upload a high-res version.</li>
              <li><strong>File types:</strong> JPEG, PNG recommended. TIFF supported but may need re-scanning.</li>
              <li><strong>Max file size:</strong> 100MB for admin uploads</li>
              <li><strong>Color analysis:</strong> Works best with JPEG/PNG files (not TIFF)</li>
            </ul>
          </div>
            </>
          )}

          {/* Artist Inventory Track */}
          {activeTrack === 'artist_inventory' && (
            <>
              {/* Inventory Info Banner */}
              <div className="status-card inventory-info">
                <div className="status-card-header">
                  <h3><Truck size={18} /> Artist-Fulfilled Prints</h3>
                  {printStatus?.artist_inventory_enabled && (
                    <span className="enabled-badge">
                      <Check size={12} /> Active
                    </span>
                  )}
                </div>
                <p className="inventory-description">
                  Sell prints from your own inventory. You handle printing and shipping, 
                  VibrantCast takes a {(commissionRate * 100).toFixed(0)}% commission.
                </p>
              </div>

              {/* Inventory Photos */}
              <div className="status-card">
                <div className="status-card-header">
                  <h3><Camera size={18} /> Product Photos</h3>
                  <small>Show buyers what they'll receive</small>
                </div>
                
                <input
                  type="file"
                  ref={inventoryPhotoInputRef}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleInventoryPhotoUpload(e.target.files?.[0])}
                />

                <div className="inventory-photos-grid">
                  {inventoryPhotos.map((photo, idx) => (
                    <div key={idx} className="inventory-photo-card">
                      <div className="photo-preview">
                        <img src={photo.url} alt={`Print ${idx + 1}`} />
                        <button 
                          className="remove-photo-btn"
                          onClick={() => removeInventoryPhoto(idx)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder="Size (e.g., 11x14)"
                        value={photo.size || ''}
                        onChange={(e) => updateInventoryPhoto(idx, 'size', e.target.value)}
                        className="photo-input"
                      />
                      <input
                        type="text"
                        placeholder="Caption (e.g., Matted, ready to frame)"
                        value={photo.caption || ''}
                        onChange={(e) => updateInventoryPhoto(idx, 'caption', e.target.value)}
                        className="photo-input"
                      />
                    </div>
                  ))}
                  
                  <button 
                    className="add-photo-btn"
                    onClick={() => inventoryPhotoInputRef.current?.click()}
                    disabled={uploadingInventoryPhoto}
                  >
                    {uploadingInventoryPhoto ? (
                      <RefreshCw className="spin" size={24} />
                    ) : (
                      <>
                        <Plus size={24} />
                        <span>Add Photo</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Inventory Sizes & Pricing */}
              <div className="status-card">
                <div className="status-card-header">
                  <h3><DollarSign size={18} /> Sizes & Pricing</h3>
                </div>
                
                <div className="inventory-sizes-list">
                  {Object.entries(inventorySizes).map(([size, data]) => (
                    <div key={size} className="inventory-size-row">
                      <span className="size-label">{size}</span>
                      <input
                        type="number"
                        placeholder="Price"
                        value={data.price || ''}
                        onChange={(e) => updateInventorySize(size, 'price', e.target.value)}
                        className="size-input"
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={data.quantity || ''}
                        onChange={(e) => updateInventorySize(size, 'quantity', e.target.value)}
                        className="size-input small"
                      />
                      <input
                        type="text"
                        placeholder="Edition name"
                        value={data.edition_name || ''}
                        onChange={(e) => updateInventorySize(size, 'edition_name', e.target.value)}
                        className="size-input"
                      />
                      <button 
                        className="remove-size-btn"
                        onClick={() => removeInventorySize(size)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="add-size-row">
                  <select 
                    id="new-size-select"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        setInventorySizes(prev => ({
                          ...prev,
                          [e.target.value]: { price: null, quantity: null, edition_name: '' }
                        }));
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">+ Add size...</option>
                    {['5x7', '8x10', '11x14', '12x12', '16x20', '18x18', '20x24', '20x30', '24x36'].map(size => (
                      !inventorySizes[size] && <option key={size} value={size}>{size}"</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Commission Settings */}
              <div className="status-card">
                <div className="status-card-header">
                  <h3><Settings size={18} /> Commission Rate</h3>
                </div>
                <div className="commission-row">
                  <label>Platform Commission:</label>
                  <select 
                    value={commissionRate} 
                    onChange={(e) => setCommissionRate(parseFloat(e.target.value))}
                  >
                    <option value="0.10">10%</option>
                    <option value="0.12">12%</option>
                    <option value="0.15">15% (default)</option>
                    <option value="0.20">20%</option>
                    <option value="0.25">25%</option>
                  </select>
                </div>
                <p className="commission-note">
                  Artist receives {((1 - commissionRate) * 100).toFixed(0)}% of each sale.
                </p>
              </div>

              {/* Save / Toggle Actions */}
              <div className="actions-card">
                <h3>Actions</h3>
                <div className="action-buttons">
                  <button
                    className="action-btn primary"
                    onClick={saveInventorySettings}
                    disabled={savingInventory}
                  >
                    {savingInventory ? (
                      <><RefreshCw className="spin" size={18} /> Saving...</>
                    ) : (
                      <><Check size={18} /> Save Inventory Settings</>
                    )}
                  </button>
                  
                  {printStatus && (
                    <button
                      className={`action-btn ${printStatus.artist_inventory_enabled ? 'danger' : 'success'}`}
                      onClick={toggleArtistInventory}
                    >
                      {printStatus.artist_inventory_enabled ? (
                        <><X size={18} /> Disable Artist Inventory</>
                      ) : (
                        <><Package size={18} /> Enable Artist Inventory</>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Tips for Artist Inventory */}
              <div className="tips-card">
                <h3><Info size={16} /> Artist Inventory Tips</h3>
                <ul>
                  <li><strong>Photos:</strong> Show the actual print product - framed, matted, packaging</li>
                  <li><strong>Pricing:</strong> Set your own prices. Platform takes {(commissionRate * 100).toFixed(0)}% commission.</li>
                  <li><strong>Quantity:</strong> Track how many you have in stock</li>
                  <li><strong>Fulfillment:</strong> You're responsible for printing & shipping</li>
                  <li><strong>Editions:</strong> Name your editions (e.g., "Artist Proof", "First Edition")</li>
                </ul>
              </div>
            </>
          )}

          {/* Quick Links */}
          <div className="quick-links">
            <button 
              className="link-btn"
              onClick={() => navigate(`/curator/artworks/${vcId}/edit`)}
            >
              <ImageIcon size={16} /> Edit Full Artwork Details
            </button>
            <button 
              className="link-btn"
              onClick={() => navigate(`/artwork/${vcId}`)}
            >
              <ExternalLink size={16} /> View Public Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintQualityPage;
